import { toast } from "sonner";

export class FalAPIService {
  constructor(apiKey) {
    this.apiKey = apiKey;
  }

  async generateImage(apiId, params) {
    const endpoint = this.getEndpointForAPI(apiId, params);

    try {
      // Special handling for different APIs
      let requestParams = { ...params };

      if (apiId === "image-editing") {
        // Configure for Image Editing - uses image_url for editing
        requestParams = {
          prompt: params.prompt || "Edit this image",
          image_url: params.image_url,
          strength: params.strength || 0.8,
          num_inference_steps: params.num_inference_steps || 20,
          guidance_scale: params.guidance_scale || 7.5,
          negative_prompt: params.negative_prompt,
          seed: params.seed,
        };
      } else if (apiId === "nano-banana") {
        // Configure for Nano banana image editing
        requestParams = {
          prompt: params.prompt,
          image_urls: params.image_urls, // Map image_url to reference_image_url
          num_images: params.num_images || 1,
          aspect_ratio: params.aspect_ratio || "1:1",
        };
        // Remove the original image_url field since we mapped it to reference_image_url
        delete requestParams.image_url;
      } else if (apiId === "pixverse-effects") {
        // Configure for Pixverse Effects - uses image_url for base64 data
        requestParams = {
          prompt: params.prompt || "Apply visual effects to this media",
          image_url: params.image_url,
          effect: params.effect || "Hug", // Default to valid effect
          negative_prompt: params.negative_prompt,
          aspect_ratio: params.aspect_ratio || "16:9",
          seed: params.seed,
        };

        // Add resolution parameter if provided
        if (params.resolution) {
          requestParams.resolution = params.resolution;
        }
      } else if (apiId === "pika-pikaffects") {
        // Configure for Pika Effects - uses image_url for base64 data
        requestParams = {
          prompt: params.prompt,
          image_url: params.image_url,
          pikaffect: params.pikaffect || "Melt",
          motion_strength: params.motion_strength || 0.5,
          negative_prompt: params.negative_prompt,
          aspect_ratio: params.aspect_ratio || "16:9",
          seed: params.seed,
        };
      } else if (apiId === "pixverse-transition") {
        // Configure for Pixverse Transition - only send supported parameters
        requestParams = {
          prompt: params.prompt,
          first_image_url: params.first_image_url,
          end_image_url: params.end_image_url,
          aspect_ratio: params.aspect_ratio || "16:9",
          resolution: params.resolution || "540p", // Default resolution
        };

        // Add optional parameters only if they have valid values
        if (params.style && params.style.trim()) {
          requestParams.style = params.style;
        }

        if (params.negative_prompt && params.negative_prompt.trim()) {
          requestParams.negative_prompt = params.negative_prompt;
        }

        if (params.seed && !isNaN(Number(params.seed))) {
          requestParams.seed = Number(params.seed);
        }
      } else if (apiId === "pixverse-image-to-video") {
        // Configure for Pixverse Image to Video
        requestParams = {
          prompt: params.prompt,
          image_url: params.image_url,
          negative_prompt: params.negative_prompt,
          aspect_ratio: params.aspect_ratio || "16:9",
          seed: params.seed,
          duration: params.duration || 5, // Default to 5 seconds
          resolution: params.resolution || "540p", // Default resolution
        };
      } else if (apiId === "flux-pro-kontext" && params.image_urls) {
        // Configure for Flux Pro Kontext Multi - uses image_urls array
        requestParams = {
          prompt: params.prompt,
          image_urls: params.image_urls,
          strength: params.strength,
          num_inference_steps: params.num_inference_steps,
          guidance_scale: params.guidance_scale,
          negative_prompt: params.negative_prompt,
          aspect_ratio: params.aspect_ratio,
          seed: params.seed,
          num_images: 1, // Explicitly set to return only 1 image
          safety_tolerance: params.safety_tolerance,
          enable_safety_checker: params.enable_safety_checker,
        };
      } else if (apiId === "flux-pro-kontext" && params.image_url) {
        // Handle single image for Flux Pro Kontext
        requestParams = {
          prompt: params.prompt,
          image_urls: [params.image_url], // Convert single image to array
          strength: params.strength,
          num_inference_steps: params.num_inference_steps,
          guidance_scale: params.guidance_scale,
          negative_prompt: params.negative_prompt,
          aspect_ratio: params.aspect_ratio,
          seed: params.seed,
          num_images: 1,
          safety_tolerance: params.safety_tolerance,
          enable_safety_checker: params.enable_safety_checker,
        };
        // Remove the single image_url field
        delete requestParams.image_url;
      } else if (apiId === "fal-ai-vidu") {
        requestParams = {
          prompt: params.prompt,
          reference_image_urls: params.image_url,
          negative_prompt: params.negative_prompt,
          aspect_ratio: params.aspect_ratio,
          seed: params.seed,
          movement_amplitude: params.movement_amplitude,
        };
        delete requestParams.image_url;
      }

      // Remove undefined values
      Object.keys(requestParams).forEach(
        (key) => requestParams[key] === undefined && delete requestParams[key]
      );

      console.log("Final request params for", apiId, ":", requestParams);

      const response = await fetch(`https://fal.run/${endpoint}`, {
        method: "POST",
        headers: {
          Authorization: `Key ${this.apiKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestParams),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error Response:", errorText);
        throw new Error(
          `API request failed: ${response.status} - ${errorText}`
        );
      }

      const result = await response.json();
      console.log("API Response for", apiId, ":", result);
      return { result, inputs: { ...requestParams, apiId } };
    } catch (error) {
      console.error("Fal API error:", error);
      toast.error("Failed to process with AI service");
      throw error;
    }
  }

  getEndpointForAPI(apiId, params) {
    switch (apiId) {
      case "image-editing":
        // Choose endpoint based on model
        if (params?.model === "cartoonify") {
          return "fal-ai/image-editing/cartoonify";
        } else if (params?.model === "expression-change") {
          return "fal-ai/image-editing/expression-change";
        } else if (params?.model === "object-removal") {
          return "fal-ai/image-editing/object-removal";
        } else if (params?.model === "background-change") {
          return "fal-ai/image-editing/background-change";
        } else if (params?.model === "hair-change") {
          return "fal-ai/image-editing/hair-change";
        } else if (params?.model === "style-transfer") {
          return "fal-ai/image-editing/style-transfer";
        }
        return "fal-ai/image-editing/baby-version";
      case "pixverse-effects":
        return "fal-ai/pixverse/v4.5/effects";
      case "pika-pikaffects":
        return "fal-ai/pika/v1.5/pikaffects";
      case "pixverse-transition":
        return "fal-ai/pixverse/v5/transition";
      case "pixverse-image-to-video":
        return "fal-ai/pixverse/v4.5/image-to-video";
      case "flux-pro-kontext":
        return "fal-ai/flux-pro/kontext/max/multi";
      case "nano-banana":
        return "fal-ai/nano-banana/edit";
      case "fal-ai-vidu":
        return "fal-ai/vidu/q1/reference-to-video";
      default:
        return "fal-ai/flux/schnell";
    }
  }
}
